import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import { WorkshopAttendance } from '@chancenwerk/workshops/entities/WorkshopAttendance.js';
import { WorkshopAttendanceCount } from '@chancenwerk/workshops/entities/WorkshopAttendanceCount.js';
import { WorkshopFilter } from '@chancenwerk/workshops/entities/WorkshopFilter.js';
import { catchError, from, of, switchMap } from 'rxjs';

export const subscribeWorkshopAttendanceCount = registerSubscription({
  type: () => [WorkshopAttendanceCount],
  operationName: 'subscribeWorkshopAttendanceCount',
  resolve: (skip: number, limit: number, filter: WorkshopFilter) => {
    return (gqlFields: BaseQLSelectionSet<WorkshopAttendanceCount>) => {
      const selector = {
        _class: WorkshopAttendance._class,
        ...filter.getMongoFilter(),
      };
      const handle = getCollectionHandle<WorkshopAttendance>(WorkshopAttendance._collectionName);
      return from(publishFilter(WorkshopAttendance._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            skip,
            limit,
          });
        }),
        switchMap(async attendances => {
          const col = await getRawCollection<EntityData<WorkshopAttendance>>(WorkshopAttendance._collectionName);
          const res = await col
            .aggregate([
              { $match: { _id: { $in: attendances.map(x => x._id) }, attendanceCount: { $gt: 0 } } },

              ...(filter.bundesland
                ? [
                    {
                      $lookup: {
                        from: Partner._collectionName,
                        localField: 'partnerID',
                        foreignField: '_id',
                        as: 'partners',
                        pipeline: [{ $project: { bundesland: 1 } }],
                      },
                    },

                    { $unwind: '$partners' },

                    { $match: { 'partners.bundesland': filter.bundesland } },
                  ]
                : []),

              {
                $group: {
                  _id: '$workshopID',
                  workshopID: { $first: '$workshopID' },
                  eventCount: { $sum: 1 },
                  attendanceCount: { $sum: '$attendanceCount' },
                  attendanceCountMin: { $min: '$attendanceCount' },
                  attendanceCountMax: { $max: '$attendanceCount' },
                },
              },
            ])
            .toArray();
          return res.map(r => new WorkshopAttendanceCount({ ...r }));
        }),
        catchError(e => {
          return of([] as WorkshopAttendanceCount[]);
        }),
      );
    };
  },
  params: [
    {
      name: 'skip',
      type: () => Int,
    },
    {
      name: 'limit',
      type: () => Int,
    },
    {
      name: 'filter',
      type: () => WorkshopFilter,
    },
  ],
});
